// Defensive collapse of issue severity onto the two-tier client model
// (critical | non_critical). The backend enum was migrated 2026-07-28 and
// rejects low/medium on write, but this runs on every response so a legacy
// row, an un-migrated environment, or a rolled-back deploy can never render
// a third tier or silently drop out of a severity filter.
//
// Applied once in the api-client response interceptor — feature code should
// never need to re-normalize.

const NON_CRITICAL = 'non_critical'

// Tallies keyed BY severity, where a legacy key carries a count that must be
// added to non_critical rather than relabelled. Exact names only: a key like
// `resolution_speed_by_severity` is a list of rows, not a tally, and is
// handled by the ordinary scalar walk.
const TALLY_KEYS = new Set(['by_severity', 'severity_breakdown'])

const LEGACY_TALLY_KEYS = ['medium', 'low']

const isPlainObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v)

// Anything that isn't critical is non-critical. Empty/null means "no severity
// set" (e.g. an echoed "All severity" filter) and must stay falsy, not become
// a real tier.
function normalizeScalar(value: unknown): unknown {
  if (typeof value !== 'string' || value === '') {
    return value
  }
  return value.toLowerCase() === 'critical' ? 'critical' : NON_CRITICAL
}

function foldTally(tally: Record<string, unknown>): void {
  for (const key of LEGACY_TALLY_KEYS) {
    const legacy = tally[key]
    if (typeof legacy === 'number') {
      const current = tally[NON_CRITICAL]
      tally[NON_CRITICAL] = (typeof current === 'number' ? current : 0) + legacy
      delete tally[key]
    }
  }
}

function walk(node: unknown): void {
  if (Array.isArray(node)) {
    node.forEach(walk)
    return
  }
  if (!isPlainObject(node)) {
    return
  }

  for (const [key, value] of Object.entries(node)) {
    if (key === 'severity') {
      node[key] = normalizeScalar(value)
    } else if (TALLY_KEYS.has(key) && isPlainObject(value)) {
      foldTally(value)
    } else {
      walk(value)
    }
  }
}

/**
 * Rewrites every `severity` field and severity tally in a parsed response body
 * onto the two-tier model, in place. Non-object bodies pass straight through.
 */
export function normalizeSeverity<T>(body: T): T {
  walk(body)
  return body
}
